"use client";

import { useState } from "react";
import { initialen } from "./formular/auswahl-konfiguration";
import { PersonenKarte, type PersonenKartenDaten } from "./personen-karte";

/**
 * Gespräch und Verlauf des Aufgaben-Details (A4′, Betreiber 14.10.2026, Musterkatalog M-011 AD6, Design-Guide `aktivitaets-feed.md`):
 * zwei Darstellungen, die der frühere gemischte `AktivitaetsFeed` ersetzt.
 *  - `GespraechsStrang`: nur Nachrichten, dicht (Avatar 28 px, Name und Zeit in einer Zeile, Text darunter), mit Tagestrennern
 *    („Gestern“, „Heute“, „13.10.2026“); älteste zuerst, neueste unten, der Composer steht direkt darunter (Aufrufer).
 *  - `VerlaufsListe`: leise Zeilen „Person hat … “ mit Uhrzeit, gebündelt je Person und Minute; „Alle n Einträge einzeln zeigen“
 *    schaltet auf die ungebündelte Anzeige um. Das Bündeln ist reine Anzeige und liegt beim Aufrufer (Daten bleiben unberührt).
 * Der Baustein bekommt fertige Zeilen (Texte, Zeiten, Tage) und kennt weder Quellen noch Rechte: der Aufrufer liefert nur, was die Person
 * lesen darf. Preline-Herkunft wie der Aktivitäts-Feed: `data-display/data-display-sub-operational-activity-views/data-display-cat-activity-feeds/collapsible-activity-feed`.
 */
export interface TagMarke {
  /** Sortier- und Gruppenschlüssel (JJJJ-MM-TT). */
  schluessel: string;
  /** Beschriftung des Trenners („Heute“). */
  text: string;
}

export interface GespraechsEintrag {
  id: string;
  autor: string | null;
  autorKarte?: PersonenKartenDaten | null;
  /** Fertig formatierte Uhrzeit („09:10“); der Tag steht im Trenner. */
  zeit: string;
  inhalt: React.ReactNode;
  geaendert?: boolean;
  /** Nachricht der angemeldeten Person (der Avatar trägt die Primärtönung). */
  eigene?: boolean;
  tag: TagMarke;
  /** DOM-Id der Zeile als Sprungziel aus dem Verlauf (W208); fehlt = kein Anker. */
  anker?: string;
  /**
   * Aktionen an der Zeile (Betreiber 19.10.2026, Abnahme Besprechungsprotokoll: „Bearbeiten“/„Löschen“ stehen am eigenen Kommentar, nicht gesammelt
   * unter dem Strang). Der Aufrufer liefert sie nur für Zeilen, die die Person ändern darf; der Baustein kennt keine Rechte. Fehlt = keine Aktionszeile.
   */
  aktionen?: React.ReactNode;
}

export interface VerlaufsEintrag {
  id: string;
  autor: string;
  autorKarte?: PersonenKartenDaten | null;
  /** Der Satz ohne Namen („hat die Priorität auf „Hoch“ gesetzt“). */
  satz: string;
  zeit: string;
  tag: TagMarke;
  /**
   * Sprung zur Nachricht (W208, Gesprächszeilen): Textlink hinter dem Satz. Der Aufrufer setzt ihn nur für Nachrichten, die die Person
   * lesen darf und die noch bestehen; der Baustein kennt keine Rechte.
   */
  sprung?: { label: string; beschriftung: string; beimKlick: () => void };
}

/** Zahl der zuerst gezeigten (neuesten) Nachrichten und der Schritt von „Ältere anzeigen“. */
export const GESPRAECH_SEITE = 20;

const AVATAR = "flex shrink-0 justify-center items-center size-7 border border-layer-line text-xs font-semibold uppercase rounded-full";
const AVATAR_FREMD = `${AVATAR} bg-layer text-layer-foreground`;
const AVATAR_EIGEN = `${AVATAR} bg-primary-100 text-primary-800`;
const LINK = "text-xs text-muted-foreground-1 hover:text-foreground underline underline-offset-2 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-focus rounded-xs";
const LINK_EINGERUECKT = "ms-3.5 text-xs text-muted-foreground-1 hover:text-foreground underline underline-offset-2 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-focus rounded-xs";

function Tagestrenner({ text }: { text: string }) {
  return (
    <li className="my-2 flex items-center gap-x-3 text-xs text-muted-foreground-1" data-tagestrenner="">
      <span className="grow h-px bg-line-2" aria-hidden="true" />
      {text}
      <span className="grow h-px bg-line-2" aria-hidden="true" />
    </li>
  );
}

export function GespraechsStrang({
  eintraege,
  leerText = "Noch keine Nachrichten.",
  beschriftung = "Gespräch",
  seite = GESPRAECH_SEITE,
  zeigeId,
}: {
  eintraege: GespraechsEintrag[];
  leerText?: string;
  beschriftung?: string;
  seite?: number;
  /** Diese Nachricht muss sichtbar sein (Sprung aus dem Verlauf, W208): „Ältere anzeigen“ wird so weit aufgeklappt, dass sie dabei ist. */
  zeigeId?: string;
}) {
  const [zeige, setZeige] = useState(seite);
  const zielIndex = zeigeId ? eintraege.findIndex((e) => e.id === zeigeId) : -1;
  const mindestens = zielIndex >= 0 ? eintraege.length - zielIndex : 0;
  const ersterIndex = Math.max(0, eintraege.length - Math.max(zeige, mindestens));
  const sichtbar = eintraege.slice(ersterIndex);
  if (eintraege.length === 0) return <p className="px-4 sm:px-5 py-4 text-sm text-muted-foreground-1">{leerText}</p>;
  return (
    <>
      {/* Preline: data-display/data-display-sub-operational-activity-views/data-display-cat-activity-feeds/collapsible-activity-feed (dichte Form mit Tagestrennern) */}
      {ersterIndex > 0 && (
        <div className="px-4 sm:px-5 pt-3">
          <button type="button" className={LINK} onClick={() => setZeige((z) => z + seite)}>
            {`Ältere anzeigen (${ersterIndex})`}
          </button>
        </div>
      )}
      <ol aria-label={beschriftung} className="px-4 sm:px-5 py-2">
        {sichtbar.map((e, i) => (
          <GespraechsZeile key={e.id} e={e} vorher={i > 0 ? sichtbar[i - 1] : undefined} />
        ))}
      </ol>
    </>
  );
}

function GespraechsZeile({ e, vorher }: { e: GespraechsEintrag; vorher: GespraechsEintrag | undefined }) {
  const neuerTag = !vorher || vorher.tag.schluessel !== e.tag.schluessel;
  return (
    <>
      {neuerTag && <Tagestrenner text={e.tag.text} />}
      <li className="flex gap-x-3 py-1.5 rounded-lg scroll-mt-4 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-focus" id={e.anker} tabIndex={e.anker ? -1 : undefined}>
        <span className={e.eigene ? AVATAR_EIGEN : AVATAR_FREMD} aria-hidden="true">
          {initialen(e.autor ?? "?")}
        </span>
        <div className="grow min-w-0">
          <p className="text-sm font-semibold text-foreground">
            {e.autorKarte ? <PersonenKarte person={e.autorKarte}>{e.autor}</PersonenKarte> : (e.autor ?? "Unbekannt")}
            <span className="ms-2 text-xs font-normal text-muted-foreground-1">{e.zeit}</span>
            {e.geaendert && <span className="ms-2 text-xs font-normal text-muted-foreground-1">bearbeitet</span>}
          </p>
          <p className="text-sm text-foreground whitespace-pre-line break-words">{e.inhalt}</p>
          {e.aktionen && (
            <div className="mt-0.5 flex flex-wrap items-center gap-x-1" data-gespraech-aktionen="">
              {e.aktionen}
            </div>
          )}
        </div>
      </li>
    </>
  );
}

export function VerlaufsListe({
  gebuendelt,
  einzeln,
  leerText = "Noch keine Änderungen.",
  beschriftung = "Verlauf",
}: {
  /** Die Zeilen je Person und Minute (Anzeige). */
  gebuendelt: VerlaufsEintrag[];
  /** Jeder Eintrag einzeln, wie gespeichert. */
  einzeln: VerlaufsEintrag[];
  leerText?: string;
  beschriftung?: string;
}) {
  const [alle, setAlle] = useState(false);
  const zeilen = alle ? einzeln : gebuendelt;
  const kannUmschalten = einzeln.length > gebuendelt.length;
  if (einzeln.length === 0) return <p className="px-4 sm:px-5 py-4 text-sm text-muted-foreground-1">{leerText}</p>;
  return (
    <>
      {/* Preline: data-display/data-display-sub-operational-activity-views/data-display-cat-activity-feeds/collapsible-activity-feed (leise Verlaufszeilen mit Uhrzeit) */}
      <ol aria-label={beschriftung} className="px-4 sm:px-5 py-2">
        {zeilen.map((z, i) => {
          const neuerTag = i === 0 || zeilen[i - 1]!.tag.schluessel !== z.tag.schluessel;
          return (
            <VerlaufsZeile key={z.id} z={z} mitTrenner={neuerTag} />
          );
        })}
      </ol>
      {kannUmschalten && (
        <div className="px-4 sm:px-5 pb-3">
          <button type="button" className={LINK_EINGERUECKT} aria-pressed={alle} onClick={() => setAlle((a) => !a)}>
            {alle ? "Gebündelt zeigen" : `Alle ${einzeln.length} Einträge einzeln zeigen`}
          </button>
        </div>
      )}
    </>
  );
}

function VerlaufsZeile({ z, mitTrenner }: { z: VerlaufsEintrag; mitTrenner: boolean }) {
  return (
    <>
      {mitTrenner && <Tagestrenner text={z.tag.text} />}
      <li className="flex items-start gap-x-2 py-1 text-xs text-muted-foreground-1">
        <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-line-3" aria-hidden="true" />
        <span className="grow min-w-0">
          <span className="font-medium text-foreground">
            {z.autorKarte ? <PersonenKarte person={z.autorKarte} groesse="xs">{z.autor}</PersonenKarte> : z.autor}
          </span>{" "}
          {z.satz}
          {z.sprung && (
            <>
              {" "}
              <button type="button" className={LINK} aria-label={z.sprung.beschriftung} onClick={z.sprung.beimKlick} data-verlauf-sprung="">
                {z.sprung.label}
              </button>
            </>
          )}
        </span>
        <span className="shrink-0 tabular-nums">{z.zeit}</span>
      </li>
    </>
  );
}
